'use client'

import { useEffect, useId, useState } from 'react'
import QRCode from 'qrcode'

export type ConnectDogCardQrCodeProps = {
  url: string
  size?: number
  className?: string
}

/**
 * High-contrast QR for in-person Dog Card handoff.
 * Encodes only the canonical public URL — no analytics payload.
 */
export function ConnectDogCardQrCode({
  url,
  size = 220,
  className,
}: ConnectDogCardQrCodeProps) {
  const labelId = useId()
  const [dataUrl, setDataUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    setFailed(false)
    setDataUrl(null)

    QRCode.toDataURL(url, {
      width: size,
      margin: 2,
      color: {
        dark: '#1A1323',
        light: '#FFFFFF',
      },
      errorCorrectionLevel: 'M',
    })
      .then((value) => {
        if (!cancelled) setDataUrl(value)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })

    return () => {
      cancelled = true
    }
  }, [url, size])

  if (failed) {
    return (
      <p className="text-center text-sm text-amber-100/90" role="status">
        QR code unavailable. Use Copy Link instead.
      </p>
    )
  }

  if (!dataUrl) {
    return (
      <div
        className="mx-auto animate-pulse rounded-2xl bg-white/10"
        style={{ width: size, height: size }}
        aria-hidden
      />
    )
  }

  return (
    <img
      src={dataUrl}
      alt=""
      aria-labelledby={labelId}
      width={size}
      height={size}
      className={className ?? 'mx-auto rounded-2xl border-4 border-white bg-white p-2 shadow-lg'}
    />
  )
}

export function ConnectDogCardQrInstruction({ id }: { id?: string }) {
  return (
    <p id={id} className="text-center text-sm leading-relaxed text-white/80">
      Ask the other dog parent to scan this code.
    </p>
  )
}
